import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/auth";
import { formatMinor, formatMinorAmount } from "@/lib/hooks";
import { ErrorNote, Spinner } from "@/components/ui";
import {
  companyHour,
  formatCompanyDate,
  formatCompanyDateTime,
  formatCompanyWeekday,
} from "@/lib/time";
import type { FinanceSummaryRow, Home } from "@/lib/types";
import { IconCar, IconClipboardCheck, IconInbox, IconTicket } from "@/components/icons";

const CARD = "nm-card p-5";

function greetingKey(hour: number): string {
  if (hour >= 5 && hour < 12) return "home.greetingMorning";
  if (hour >= 12 && hour < 18) return "home.greetingDay";
  return "home.greetingEvening";
}

/** The round KPI icon colours (background / icon). */
const KPI_TONE = {
  orange: "bg-[#FFF1E6] text-[#C2410C]",
  teal: "bg-[#E0F2EF] text-[#1E6B65]",
  violet: "bg-[#EEE9FB] text-[#5B3FA8]",
  blue: "bg-[#E6F0FB] text-[#1D4E89]",
} as const;

function Kpi({
  label,
  value,
  icon,
  tone,
  children,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone: keyof typeof KPI_TONE;
  children?: React.ReactNode;
}) {
  return (
    <div className={CARD + " relative"}>
      <span
        className={`absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full ${KPI_TONE[tone]}`}
      >
        {icon}
      </span>
      <div className="min-h-[2.5rem] pr-12 text-sm font-medium leading-5 text-ink-muted">{label}</div>
      <div className="mt-2 text-[38px] font-extrabold leading-none tracking-tight text-ink">
        {value}
      </div>
      {children != null && <div className="mt-3 text-sm text-ink-muted">{children}</div>}
    </div>
  );
}

function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-3 text-base font-bold text-ink">{children}</h2>;
}

/** An empty attention list: a check in a circle and why nothing is listed. */
function AllClear({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3 py-2 text-sm text-ink-muted">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M3.5 8.5l3 3 6-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {text}
    </div>
  );
}

/** This Dubai month's money. Owner rule 2: «Заработано» (completed lines) and
 * «Ожидается» (the rest) are two figures, never one; the bar is how much of
 * the month's lines is already earned. */
function FinanceCard({ rows }: { rows: FinanceSummaryRow[] }) {
  const { t } = useTranslation();
  const row =
    rows.find((r) => r.currency === "AED") ??
    rows[0] ?? {
      currency: "AED",
      earned_revenue_minor: 0,
      earned_profit_minor: 0,
      expected_revenue_minor: 0,
      expected_profit_minor: 0,
    };
  const others = rows.filter((r) => r !== row);
  const total = row.earned_revenue_minor + row.expected_revenue_minor;
  const pct = total > 0 ? Math.min(100, Math.round((row.earned_revenue_minor / total) * 100)) : 0;
  const block = (label: string, revenue: number, profit: number) => (
    <div className="min-w-0">
      <div className="text-sm font-medium text-white/95">{label}</div>
      <div className="mt-1 break-words text-[28px] font-extrabold leading-tight [text-shadow:0_1px_2px_rgba(16,60,56,0.25)]">
        {formatMinorAmount(revenue)}
      </div>
      <div className="mt-1 text-xs text-white/95">
        {t("home.earnedProfit", { amount: formatMinorAmount(profit) })}
      </div>
    </div>
  );
  return (
    <div className="rounded-card bg-gradient-to-br from-[#1E6B65] from-55% to-[#2A9D8F] p-5 text-white shadow-[0_2px_4px_rgba(30,107,101,0.18),0_14px_32px_rgba(30,107,101,0.32)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-bold">{t("home.financeTitle")}</h2>
        <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-bold">
          {row.currency}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {block(t("finance.earned"), row.earned_revenue_minor, row.earned_profit_minor)}
        {block(t("finance.expected"), row.expected_revenue_minor, row.expected_profit_minor)}
      </div>
      <div className="mt-5">
        <div
          className="h-2 overflow-hidden rounded-full bg-white/20"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2">
          <span className="text-xs font-medium text-white">
            {t("home.receivedOfExpected", {
              received: formatMinorAmount(row.earned_revenue_minor),
              total: formatMinorAmount(total),
              currency: row.currency,
            })}
          </span>
        </div>
        {others.length > 0 && (
          <div className="mt-1 text-xs text-white/95">
            {t("home.otherCurrencies", {
              list: others
                .map((r) => formatMinor(r.earned_revenue_minor + r.expected_revenue_minor, r.currency))
                .join(" · "),
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { me, can } = useAuth();
  const q = useQuery({
    queryKey: ["home"],
    queryFn: () => api.get<Home>("/home"),
  });

  const now = new Date();
  const firstName = (me?.name ?? "").trim().split(/\s+/)[0] ?? "";
  const header = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink-muted">
          {formatCompanyWeekday(now, i18n.resolvedLanguage)}
        </div>
        <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-[28px]">
          {t(greetingKey(companyHour(now)), { name: firstName })}
        </h1>
      </div>
      <div className="flex flex-wrap gap-2">
        {can("leads:read") && (
          <Link
            to="/leads"
            className="inline-flex h-11 items-center rounded-xl border border-[#1E6B65] bg-surface px-4 text-sm font-semibold text-[#1E6B65] shadow-[0_1px_2px_rgba(16,60,56,0.06)] transition hover:bg-[#E0F2EF]"
          >
            {t("home.newLead")}
          </Link>
        )}
        {can("bookings:write") && (
          <Link
            to="/bookings/quick"
            className="inline-flex h-11 items-center rounded-xl bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-hover"
          >
            + {t("home.newBooking")}
          </Link>
        )}
      </div>
    </div>
  );

  if (q.isLoading)
    return (
      <div className="space-y-6">
        {header}
        <Spinner />
      </div>
    );
  if (q.isError)
    return (
      <div className="space-y-6">
        {header}
        <ErrorNote message={(q.error as Error).message} />
      </div>
    );

  const h = q.data!;
  const hot = h.leads?.by_level.HOT ?? 0;
  const warm = h.leads?.by_level.WARM ?? 0;

  return (
    <div className="space-y-6">
      {header}

      <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(210px,1fr))]">
        {h.leads && (
          <Kpi
            label={t("home.newLeadsKpi")}
            value={h.leads.total_7d}
            icon={<IconInbox size={20} />}
            tone="orange"
          >
            <div className="flex flex-wrap gap-1.5">
              <Pill className="bg-[#FCE7DA] text-[#8A3A12]">{t("home.hot", { count: hot })}</Pill>
              <Pill className="bg-page text-ink-muted">{t("home.warm", { count: warm })}</Pill>
            </div>
          </Kpi>
        )}
        <Kpi
          label={t("home.openBookings")}
          value={h.bookings.open_total}
          icon={<IconTicket size={20} />}
          tone="teal"
        >
          {t("home.upcoming", { count: h.bookings.by_status.upcoming ?? 0 })}
        </Kpi>
        {h.operations && (
          <>
            <Kpi
              label={t("home.unassignedTasks")}
              value={h.operations.unassigned_tasks}
              icon={<IconClipboardCheck size={20} />}
              tone="violet"
            >
              {h.operations.unassigned_tasks === 0 ? (
                <span className="font-semibold text-emerald-700">{t("home.allAssigned")}</span>
              ) : null}
            </Kpi>
            <Kpi
              label={t("home.pickups")}
              value={`${h.operations.pickups_today} / ${h.operations.pickups_tomorrow}`}
              icon={<IconCar size={20} />}
              tone="blue"
            >
              {t("home.todayTomorrow")}
            </Kpi>
          </>
        )}
      </div>

      {(h.finance_month || h.pending_commissions) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {h.finance_month && <FinanceCard rows={h.finance_month} />}
          {h.pending_commissions && (
            <div className={CARD + " flex flex-col"}>
              <CardTitle>{t("home.pendingCommissions")}</CardTitle>
              {h.pending_commissions.length === 0 ? (
                <p className="text-sm text-ink-muted">{t("home.noneYet")}</p>
              ) : (
                <ul className="divide-y divide-line">
                  {h.pending_commissions.map((c) => {
                    const name = c.agent_name ?? t("home.agent");
                    return (
                      <li key={c.id} className="flex items-center gap-3 py-2.5">
                        <span
                          aria-hidden="true"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-bold text-accent"
                        >
                          {name.trim().charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-ink">{name}</div>
                          {c.booking_number && (
                            <Link
                              to={`/bookings/${c.booking_number}`}
                              className="text-xs text-ink-muted hover:text-accent"
                            >
                              {c.booking_number}
                            </Link>
                          )}
                        </div>
                        <span className="shrink-0 text-sm font-bold text-ink">
                          {formatMinor(c.owed_minor, c.currency)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {can("agents:read") && (
                <Link
                  to="/agents"
                  className="mt-auto pt-3 text-sm font-semibold text-accent hover:text-accent-hover"
                >
                  {t("home.allCommissions")}
                </Link>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className={CARD}>
          <CardTitle>{t("home.awaitingApproval")}</CardTitle>
          {h.bookings.awaiting_approval.length === 0 ? (
            <AllClear text={t("home.approvalClear")} />
          ) : (
            <ul className="divide-y divide-line text-sm">
              {h.bookings.awaiting_approval.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                  <Link
                    className="font-semibold text-ink hover:text-accent"
                    to={`/bookings/${b.booking_number}`}
                  >
                    {b.booking_number}
                  </Link>
                  <span className="min-w-0 truncate text-ink-muted">{b.customer_name ?? "—"}</span>
                  <span className="ml-auto text-xs text-ink-muted">
                    {formatCompanyDate(b.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {h.operations && (
          <div className={CARD}>
            <CardTitle>{t("home.unassignedTasks")}</CardTitle>
            {h.operations.unassigned_task_list.length === 0 ? (
              <AllClear text={t("home.tasksClear")} />
            ) : (
              <ul className="divide-y divide-line text-sm">
                {h.operations.unassigned_task_list.map((task) => (
                  <li key={task.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                    <Link
                      className="font-medium text-ink hover:text-accent"
                      to={`/bookings/${task.booking_number ?? task.booking_id}`}
                    >
                      {task.service ?? t("home.task")}
                    </Link>
                    {/* The block is already "unassigned tasks" — a status pill
                        on every row would only repeat its title. */}
                    {task.booking_number && (
                      <span className="text-xs text-ink-muted">{task.booking_number}</span>
                    )}
                    <span className="ml-auto text-xs text-ink-muted">
                      {task.pickup_time
                        ? formatCompanyDateTime(task.pickup_time)
                        : t("home.noPickup")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
